/**
 * Device Master — coffre local chiffré (SANDBOX · Patrimoine).
 * AES-GCM 256 via WebCrypto. La clé maître vit UNIQUEMENT sur cet appareil
 * (localStorage) ; les valeurs financières ne quittent jamais le device en
 * clair — seuls des agrégats anonymisés partent vers le Majordome.
 */

const KEY_SLOT = 'psu_device_master_v1';
const VAULT_SLOT = 'psu_patrimoine_vault_v1';

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

async function getDeviceKey() {
  let raw = localStorage.getItem(KEY_SLOT);
  if (!raw) {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    raw = b64(await crypto.subtle.exportKey('raw', key));
    localStorage.setItem(KEY_SLOT, raw);
  }
  return crypto.subtle.importKey('raw', unb64(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

export async function saveVault(assets) {
  const key = await getDeviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(assets));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  localStorage.setItem(VAULT_SLOT, JSON.stringify({ iv: b64(iv), ct: b64(ct) }));
}

export async function loadVault() {
  const blob = localStorage.getItem(VAULT_SLOT);
  if (!blob) return null;
  try {
    const { iv, ct } = JSON.parse(blob);
    const key = await getDeviceKey();
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(iv) }, key, unb64(ct));
    return JSON.parse(new TextDecoder().decode(pt));
  } catch { return null; }
}

const DEMO_ASSETS = [
  { id: 'a1', layer: 'immobilier', name: 'Appartement Paris 9e', lat: 48.8767, lng: 2.34, value: 420000, perf: 3.1, region: 'Île-de-France', detail: 'T3 · 64 m² · loué 1 750 €/mois' },
  { id: 'a2', layer: 'immobilier', name: 'Immeuble Lyon Croix-Rousse', lat: 45.7746, lng: 4.832, value: 780000, perf: 5.4, region: 'Auvergne-Rhône-Alpes', detail: '4 lots · rendement brut 6,2 %' },
  { id: 'a3', layer: 'immobilier', name: 'Maison Bordeaux Caudéran', lat: 44.8578, lng: -0.6109, value: 515000, perf: -1.2, region: 'Nouvelle-Aquitaine', detail: 'Résidence secondaire · 142 m²' },
  { id: 'b1', layer: 'bourse', name: 'PEA — Euronext Paris', lat: 48.8698, lng: 2.3411, value: 86500, perf: 12.4, region: 'Île-de-France', detail: 'CAC 40 ETF 60 % · valeurs tech 40 %' },
  { id: 'b2', layer: 'bourse', name: 'CTO — La Défense', lat: 48.8918, lng: 2.2362, value: 42300, perf: 8.7, region: 'Île-de-France', detail: 'Actions US · dividendes réinvestis' },
  { id: 'l1', layer: 'liquidites', name: 'Livret A — Rouen', lat: 49.4431, lng: 1.0993, value: 22950, perf: 3.0, region: 'Normandie', detail: 'Plafond quasi atteint' },
  { id: 'l2', layer: 'liquidites', name: 'Compte courant Paris 8e', lat: 48.8742, lng: 2.3089, value: 8400, perf: 0, region: 'Île-de-France', detail: 'Trésorerie du foyer' },
  { id: 'p1', layer: 'pro', name: "Peter's Universe SAS — Station F", lat: 48.834, lng: 2.3706, value: 150000, perf: 22.0, region: 'Île-de-France', detail: 'Parts sociales · SaaS immobilier' },
  { id: 'p2', layer: 'pro', name: 'Atelier Nantes', lat: 47.2184, lng: -1.5536, value: 35000, perf: 4.5, region: 'Pays de la Loire', detail: 'Local professionnel · bail 3-6-9' },
];

export async function loadOrSeedVault() {
  const existing = await loadVault();
  if (existing?.length) return existing;
  await saveVault(DEMO_ASSETS);
  return DEMO_ASSETS;
}

export const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

/** Agrégats anonymisés (aucun nom, aucune adresse, buckets de valeur). */
export function anonymizedAggregates(assets) {
  if (!assets.length) return { count: 0 };
  const total = assets.reduce((s, a) => s + a.value, 0);
  const bucket = total < 50000 ? '< 50 k€' : total < 250000 ? '50–250 k€'
    : total < 1000000 ? '250 k€–1 M€' : '> 1 M€';
  const sorted = [...assets].sort((a, b) => b.perf - a.perf);
  return {
    count: assets.length,
    total_bucket: bucket,
    breakdown: `${assets.length} position(s), médiane ${eur(sorted[Math.floor(sorted.length / 2)].value)}`,
    best_perf: `+${sorted[0].perf}%`,
    worst_perf: `${sorted[sorted.length - 1].perf}%`,
    regions: [...new Set(assets.map((a) => a.region))].join(', '),
  };
}
